import React from 'react'
import { createTheme, adaptV4Theme, ComponentsOverrides } from '@mui/material/styles'

import { ThemeContants } from './_props'
import { ArrowDownIcon } from '../../assets/icons'

const transmissionPalette = {
    main: '#575BAF',
    light: '#CACCFC',
    contrastText: '#ffffff',
    dark: '#373C99',
    ultraLight: '#E7E8FF',
    ultraUltraLight: '#F3F4FC',
    blueDark: '#7B8DA8',
}

export const themeConstants: ThemeContants = {
    formats: {
        defaultMinWidth: '500px',
        defaultMaxWidth: '700px',
        fullMaxWidth: '90vw',
        breakpoint: 'md',
    },
    MedicalActContainerIdleMobileMaxHeight: '12.5rem',
    primaryNavigationShift: '4rem',
    callDesktopHeight: '85vh',
}

const doctorMainPalette = {
    primary: transmissionPalette,
    secondary: {
        mediumLighter: '#F0EEF9',
        main: '#24c3cf',
        light: '#65CFD7',
        lighter: '#F7FEFE',
        dark: '#1A939C',
        mediumLight: '#F0FBFC',
        ultraLight: '#D6F9FC',
        ultraLighter: 'rgba(36, 195, 207, 0.03)',
        contrastText: '#ffffff',
        blueDark: '#7B8DA8',
        ultraDark: '#2E0DA6',
    },
    common: {
        main: '#24C3CF',
        hover: '#50DEE9',
        ultraLight: '#E7F2F9',
        ultraLighter: '#E9FAFB',
        ultraFreackingLight: '#F6FDFD',
        lighter: '#F7FEFE',
    },
}
const transmissionMainPalette = {
    primary: {
        main: '#24c3cf',
        light: '#65CFD7',
        dark: '#1A939C',
        ultraLight: '#D6F9FC',
        contrastText: '#ffffff',
    },
    secondary: transmissionPalette,
}

const typographyPalette = {
    dark: '#000000',
    main: '#313336',
    half: '#808489',
    halfer: '#4E5054',
    medium: '#707276',
    placeholder: '#DDE1EC',
    light: '#676767',
    lighter: '#979797',
    blueDark: '#7B8DA8',
}

export const palette = {
    logo: {
        main: '#24c3cf',
    },
    ...doctorMainPalette,
    mainTitleBar: {
        main: '#2f3644',
    },
    consultation: {
        main: '#52cb94',
        dark: '#37B87E',
        light: '#95DFBE',
        ultraLight: '#D4FBE9',
        contrastText: '#ffffff',
    },
    transmission: transmissionPalette,
    editor: {
        main: '#24c3cf',
        light: '#65CFD7',
        dark: '#1A939C',
        ultraLight: '#D6F9FC',
        contrastText: '#ffffff',
    },
    textPrimaryColor: {
        main: '#4e4c57',
    },
    lightGrey: {
        ultraLight: '#FCFBFE',
        light: '#FBFBFE',
        thinLight: '#FAFAFB',
        main: '#f4f4fb',
        dark: '#E5E5F5',
        darker: '#C1C1D1',
        darkest: '#9CA7A8',
        hard: '#9EABBF',
        half: '#EBEEF5',
        halfMediumLight: '#E0E0E0',
        halfLight: '#FBFBFD',
        border: '#dde3e3',
        borderMedium: '#e5e9f2',
        borderDark: '#F5F4FB',
        gradient: 'linear-gradient(to bottom, #f4f4fb 65%, rgba(244,244,251, 0))',
    },
    lighterGrey: {
        main: '#f9f9f9',
    },
    darkGrey: {
        main: '#7f8297',
        dark: '#5D6977',
        ultraDarkDarkGrey: '#313336',
        ultraDarkDarkerGrey: '#45474B',
    },
    hardGrey: {
        main: '#545454',
        light: '#696969',
    },
    mediumGrey: {
        main: '#666b6a',
        dark: '#2c2e2d',
    },
    blue: {
        main: '#00aafc',
        light: '#6EB7F7',
        ultraLight: '#e3f5ff',
        dark: '#7B8DA8',
    },
    white: {
        main: '#ffffff',
    },
    green: {
        main: '#52CB94',
        contrastText: '#ffffff',
        lightBgLabel: '#CCEBDD',
        dark: '#22724E',
    },
    orange: {
        main: '#f4511e',
        ultraLight: '#FFE0D7',
        ultraVeryLight: '#FFF3E5',
        ultraUltraLight: '#fff2ee',
        warning: '#FB7D08',
    },
    red: {
        main: '#ef4141',
        ultraLight: '#fde8e8',
    },
    yellow: {
        main: '#FFD98F',
        dark: '#F5B95F',
    },
    grey: {
        main: '#47525E',
        darkGrey: '#555855',
        ultraDarkGrey: '#343F4B',
    },
    pink: {
        main: '#fd92b4',
    },
    purple: {
        main: '#7989E2',
        light: '#949FCE',
        ultraLight: '#F1F2F9',
        contrastText: '#ffffff',
        lightBgLabel: '#DCDEF9',
        dark: '#484C9D',
    },
    hardRedRose: {
        main: '#fb355d',
        ultraLight: '#FFCED8',
        ultraUltraLight: '#FAF1F3',
        ultraLightRgba: 'rgba(251,53,93,0.08)',
        light: '#F44E70',
    },
    error: {
        main: '#fb355d',
        ultraLight: '#FFCED8',
        ultraUltraLight: '#FAF1F3',
        ultraLightRgba: 'rgba(251,53,93,0.08)',
        light: '#F44E70',
    },
    lightBrown: {
        main: '#928686',
        light: '#B6AFAF',
    },
    pinkRed: {
        main: '#fb355d',
        ultraLight: '#FFCED8',
        light: '#F44E70',
    },
    typography: typographyPalette,
}

const defaultTheme = createTheme(
    adaptV4Theme({
        typography: {
            fontFamily: "'Averta'",
            fontWeightRegular: 600,
            fontWeightLight: 'normal',
            fontWeightBold: 'bold',
            fontSize: 16,
            // @ts-expect-error TS(2322) FIXME: Type '{ fontFamily: "'Averta'"; fontWeightRegular:... Remove this comment to see the full error message
            lineHeight: '0.8',
            h1: {
                fontSize: '2.1rem',
                fontWeight: 'bold',
            },
            h2: {
                fontSize: '1.8rem',
                fontWeight: 700,
            },
            h3: {
                fontSize: '1.3rem',
                fontWeight: 700,
            },
            h4: {
                fontSize: '1.2rem',
                fontWeight: 700,
            },
            h5: {
                fontSize: '1.1rem',
                fontWeight: 700,
            },
            h6: {
                fontSize: '1rem',
                fontWeight: 600,
            },
            body1: {
                fontSize: '1rem',
                fontWeight: 600,
            },
        },
        constants: themeConstants,
        palette: palette as any,
    }),
)

const overrides: ComponentsOverrides = {
    MuiPaper: {
        root: {
            borderRadius: '8px !important',
        },
        elevation1: {
            boxShadow: 'none',
        },
    },
    MuiSkeleton: {
        root: {
            background: palette.lightGrey.hard,
            borderRadius: '.5rem',
            transform: 'none',
        },
    },
    MuiFormControl: {
        root: {
            display: 'flex',
            textAlign: 'start',
            [defaultTheme.breakpoints.down('sm')]: {
                justifyContent: 'center',
            },
        },
    },
    MuiCardHeader: {
        root: {
            textAlign: 'left',
            paddingLeft: '10px',
            paddingBottom: '0px',
        },
        title: {
            fontWeight: 'bold',
            fontSize: '22px',
            color: palette.textPrimaryColor.main,
            [defaultTheme.breakpoints.down('sm')]: {
                fontSize: '16px',
            },
        },
    },
    MuiCardContent: {
        root: {
            padding: '10px',
        },
    },
    MuiTooltip: {
        popper: {
            zIndex: '2501 !important',
        },
        tooltip: {
            color: '#000000',
            backgroundColor: '#FFFFFF',
            border: `1px solid ${palette.lightGrey.dark}`,
            boxShadow: '0 6px 14px 0 rgba(158, 171, 191, 0.3)',
            borderRadius: '8px',
            paddingTop: '.5rem',
            paddingBottom: '.5rem',
            fontWeight: 600,
            lineHeight: '1.09',
            fontSize: '.7rem',
        },
        arrow: {
            color: '#FFFFFF',
            textShadow: '0px 0px 3px #000',
            '&::before': {
                border: `1px solid ${palette.lightGrey.dark}`,
                borderRadius: '2px',
            },
        },
    },
    MuiButton: {
        containedError: {
            background: palette.hardRedRose.main,
            '&:hover': {
                background: palette.hardRedRose.light,
            },
        },
        root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 16,
        },
        contained: {
            borderRadius: '8px',
            color: `${defaultTheme.palette.secondary.contrastText} !important`,
            padding: '1.1rem',
            '&.MuiButton-sizeSmall': {
                padding: '.6rem 1rem',
            },
        },
        outlined: {
            color: `${palette.darkGrey.main}`,
        },
        text: {
            fontWeight: 600,
            fontSize: 16,
        },
    },
    MuiListItem: {
        root: {
            color: typographyPalette.main,
            fontWeight: 600,
            lineHeight: 1.5,
        },
    },
    MuiInput: {
        root: {
            '&.Mui-error': {
                color: 'red',
            },
        },
    },
    MuiInputBase: {
        root: {
            borderRadius: '8px',
            border: '2px solid',
            borderColor: '#dde3e3',
            transition: 'border-color 200ms',
            '&.Mui-focused': {
                border: '2px solid',
                borderColor: defaultTheme.palette.secondary.main,
            },
            '&.Mui-error': {
                borderColor: 'red',
            },
            backgroundColor: '#FFF',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#000000 !important',
            },
        },
        input: {
            'label + &': {
                marginTop: defaultTheme.spacing(3),
            },
            padding: '.8rem .8rem !important',
            position: 'relative',
        },
    },
    MuiSelect: {
        select: {
            borderRadius: '8px',
            background: '#ffffff',
            paddingRight: '3rem !important',
        },
        icon: {
            right: '.5rem',
            top: '50%',
            width: '16px',
            height: '7px',
            transform: 'translateY(-50%)',
        },
    },
    MuiInputLabel: {
        root: {
            fontSize: '1.1rem',
            color: palette.typography.main,
            '&.Mui-focused': {
                color: palette.typography.dark,
            },
            fontWeight: 600,
        },
    },
    MuiAlert: {
        root: {
            padding: '.5rem 1rem',
            borderRadius: '8px',
            boxShadow: '0 6px 14px 0 rgba(158, 171, 191, 0.3)',
            fontSize: '.9em',
            lineHeight: '2',
        },
        message: {
            color: '#000',
            fontWeight: 600,
            fontSize: '0.875em',
        },
        action: {
            color: '#000',
        },
        standardSuccess: {
            backgroundColor: '#FFFFFF',
        },
        standardInfo: {
            backgroundColor: '#FFFFFF',
        },
        standardWarning: {
            backgroundColor: '#FFFFFF',
        },
        standardError: {
            backgroundColor: '#FFFFFF',
        },
        outlinedSuccess: {
            backgroundColor: '#FFFFFF',
        },
        outlinedInfo: {
            backgroundColor: '#FFFFFF',
        },
        outlinedWarning: {
            backgroundColor: '#FFFFFF',
        },
        outlinedError: {
            backgroundColor: '#FFFFFF',
        },
        filledSuccess: {
            backgroundColor: '#FFFFFF',
        },
        filledInfo: {
            backgroundColor: '#FFFFFF',
        },
        filledWarning: {
            backgroundColor: '#FFFFFF',
        },
        filledError: {
            backgroundColor: '#FFFFFF',
        },
    },
    MuiCircularProgress: {
        root: {
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: `3px solid ${palette.secondary.ultraLight}`,
                borderRadius: '100px',
                zIndex: 1,
            },
        },
        svg: {
            position: 'relative',
            zIndex: 100,
        },
        circle: {
            stroke: defaultTheme.palette.secondary.main,
            strokeLinecap: 'round',
            stokeWidth: '3px',
        },
    },
    MuiBackdrop: {
        root: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
    },
    MuiTab: {
        root: {
            textTransform: 'none',
        },
    },
    MuiAutocomplete: {
        input: {
            // padding: '.8rem .8rem !important',
        },
        popper: {
            zIndex: '9999999 !important',
        },
        popupIndicator: {
            '& svg': {
                width: '16px',
                height: '7px',
            },
        },
    },
    MuiFormControlLabel: {
        label: {
            color: 'black',
        },
    },
    MuiPopover: {
        root: {
            zIndex: '9999999 !important',
        },
    },
    MuiLink: {
        root: {
            fontFamily: 'Averta',
        },
    },
    MuiDialog: {
        paperWidthSm: {
            margin: '0 !important',
        },
    },
    MuiTypography: {
        root: {
            color: typographyPalette.main,
        },
    },
    MuiAccordion: {
        root: {
            '& .MuiAccordionSummary-expandIconWrapper': {
                color: 'inherit',
                '& svg': {
                    width: '2rem',
                    height: '2rem',
                },
            },
            '&::before': {
                display: 'none !important',
            },
            margin: '0 !important',
        },
    },
    MuiAccordionSummary: {
        root: {
            minHeight: '0',
            padding: '1rem',
            '&.Mui-expanded': {
                minHeight: '0',
            },
            '& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content': {
                margin: 0,
            },
        },
    },
    MuiLinearProgress: {
        root: {
            width: '100%',
            height: '.2rem',
            borderRadius: '100px',
            '& .MuiLinearProgress-bar': {
                borderRadius: '100px',
                minWidth: '35%',
            },
        },
    },
}

const props = {
    MuiInput: {
        disableUnderline: true,
    },
    MuiInputLabel: {
        shrink: true,
    },
    MuiSelect: {
        IconComponent: ArrowDownIcon,
    },
    MuiAutocomplete: {
        popupIcon: <ArrowDownIcon />,
    },
    MuiTooltip: {
        arrow: true,
    },
}

const commonThemesProps = {
    constants: (defaultTheme as any).constants,
    typography: defaultTheme.typography,
    overrides,
    props,
}

export const transmissionTheme = {
    palette: {
        ...defaultTheme.palette,
        ...transmissionMainPalette,
    },
    ...commonThemesProps,
}

const doctorTheme = {
    palette: defaultTheme.palette,
    ...commonThemesProps,
}

export default doctorTheme
